import React, { useState, useRef, useCallback, useEffect } from "react";
import { API_URL } from "../config";
import "./ImageComposer.css";

function getToken() {
  return localStorage.getItem("token") || "";
}

const SLOTS = [
  { key: "image0", label: "@image0" },
  { key: "image1", label: "@image1" },
  { key: "image2", label: "@image2" },
  { key: "image3", label: "@image3" },
];

const STORAGE_KEY = "720mt_composer";

function loadSession() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch { return null; }
}

function saveSession(data) {
  try { localStorage.setItem(STORAGE_KEY, JSON.stringify(data)); } catch {}
}

// Convierte un dataURL a File
function dataUrlToFile(dataUrl, name) {
  const [header, b64] = dataUrl.split(",");
  const mime = header.match(/:(.*?);/)?.[1] || "image/png";
  const bytes = atob(b64);
  const arr = new Uint8Array(bytes.length);
  for (let i = 0; i < bytes.length; i++) arr[i] = bytes.charCodeAt(i);
  return new File([arr], name, { type: mime });
}

export default function ImageComposer() {
  const saved = useRef(loadSession());

  // Restaurar imágenes desde session (preview dataURLs → File)
  const restoreImages = () => {
    const s = saved.current;
    if (!s?.images) return [null, null, null, null];
    return s.images.map((entry, idx) => {
      if (!entry?.preview) return null;
      try {
        const file = dataUrlToFile(entry.preview, `restored_${idx}.png`);
        return { file, preview: entry.preview };
      } catch { return null; }
    });
  };

  const [images, setImages] = useState(restoreImages);
  const [draggingSlot, setDraggingSlot] = useState(-1);
  const fileRefs = [useRef(null), useRef(null), useRef(null), useRef(null)];

  const [prompt, setPrompt] = useState(saved.current?.prompt || "");
  const [processing, setProcessing] = useState(false);
  const [error, setError] = useState(null);
  const [resultImage, setResultImage] = useState(null);

  const [saving, setSaving] = useState(false);
  const [saved2, setSaved2] = useState(false);

  // Auto-save session
  useEffect(() => {
    saveSession({
      prompt,
      images: images.map((img) => img ? { preview: img.preview } : null),
    });
  }, [prompt, images]);

  // --- Handle file for a slot ---
  const handleFile = useCallback((file, slotIdx) => {
    if (!file || !file.type.startsWith("image/")) return;
    const reader = new FileReader();
    reader.onload = (e) => {
      setImages((prev) => {
        const next = [...prev];
        next[slotIdx] = { file, preview: e.target.result };
        return next;
      });
    };
    reader.readAsDataURL(file);
  }, []);

  const removeImage = (slotIdx) => {
    setImages((prev) => {
      const next = [...prev];
      next[slotIdx] = null;
      return next;
    });
  };

  const onDrop = (e, slotIdx) => {
    e.preventDefault();
    setDraggingSlot(-1);
    const file = e.dataTransfer.files[0];
    handleFile(file, slotIdx);
  };

  // --- Polling ---
  const pollJob = async (jobId) => {
    const token = getToken();
    for (let i = 0; i < 90; i++) {
      await new Promise((r) => setTimeout(r, 3000));
      const res = await fetch(`${API_URL}/composer/${encodeURIComponent(jobId)}/status`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json();
      if (data.status === "completed") {
        const resultRes = await fetch(`${API_URL}/composer/${encodeURIComponent(jobId)}/result`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        return await resultRes.json();
      }
      if (data.status === "error") {
        throw new Error(data.error || "Error en la generacion");
      }
    }
    throw new Error("Timeout: la generacion tardo demasiado");
  };

  // --- Generate ---
  const handleGenerate = async () => {
    const hasImages = images.some((img) => img !== null);
    if (!hasImages || !prompt.trim()) return;

    setProcessing(true);
    setError(null);
    setResultImage(null);

    try {
      const token = getToken();
      const formData = new FormData();
      formData.append("prompt", prompt.trim());
      formData.append("seed", -1);

      images.forEach((img, idx) => {
        if (img) {
          formData.append(`image${idx}`, img.file);
        }
      });

      const res = await fetch(`${API_URL}/composer/generate`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` },
        body: formData,
      });
      const text = await res.text();
      let data;
      try { data = JSON.parse(text); } catch { throw new Error(`Respuesta no valida del servidor (${res.status}): ${text.slice(0, 200)}`); }
      if (!res.ok) throw new Error(data.detail || "Error al enviar");

      const result = await pollJob(data.job_id);
      setResultImage(result.image);
    } catch (e) {
      setError(e.message);
    } finally {
      setProcessing(false);
    }
  };

  // --- Use result as input in slot ---
  const handleUseAsInput = async () => {
    if (!resultImage) return;
    const blob = await fetch(`data:image/png;base64,${resultImage}`).then((r) => r.blob());
    const file = new File([blob], `composed_${Date.now()}.png`, { type: "image/png" });
    const preview = `data:image/png;base64,${resultImage}`;

    const emptyIdx = images.findIndex((img) => img === null);
    const targetIdx = emptyIdx >= 0 ? emptyIdx : 0;
    setImages((prev) => {
      const next = [...prev];
      next[targetIdx] = { file, preview };
      return next;
    });
    setResultImage(null);
  };

  // --- Download ---
  const handleDownload = () => {
    if (!resultImage) return;
    const link = document.createElement("a");
    link.href = `data:image/png;base64,${resultImage}`;
    link.download = `composer_${Date.now()}.png`;
    link.click();
  };

  // --- Save to Biblioteca ---
  const handleSave = async () => {
    if (!resultImage || saving) return;
    setSaving(true);
    try {
      const token = getToken();
      const blob = await fetch(`data:image/png;base64,${resultImage}`).then((r) => r.blob());
      const file = new File([blob], `composer_${Date.now()}.png`, { type: "image/png" });
      const formData = new FormData();
      formData.append("file", file);
      const res = await fetch(`${API_URL}/images/upload`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` },
        body: formData,
      });
      if (res.ok) {
        setSaved2(true);
        setTimeout(() => setSaved2(false), 3000);
      }
    } catch (e) {
      setError("Error al guardar: " + e.message);
    } finally {
      setSaving(false);
    }
  };

  const hasAnyImage = images.some((img) => img !== null);

  return (
    <div className="imc-page">
      {/* Panel izquierdo: slots + prompt */}
      <div className="imc-panel--left">
        <div className="imc-section">
          <div className="imc-section-title">Imagenes de referencia</div>
          <div className="imc-slots">
            {SLOTS.map((slot, idx) => (
              <div
                key={slot.key}
                className={`imc-slot ${draggingSlot === idx ? "dragging" : ""} ${images[idx] ? "has-image" : ""}`}
                onDragOver={(e) => { e.preventDefault(); setDraggingSlot(idx); }}
                onDragLeave={() => setDraggingSlot(-1)}
                onDrop={(e) => onDrop(e, idx)}
                onClick={() => !images[idx] && fileRefs[idx].current?.click()}
              >
                <span className="imc-slot-label">{slot.label}</span>
                {images[idx] ? (
                  <>
                    <img src={images[idx].preview} alt={slot.label} />
                    <button
                      className="imc-slot-remove"
                      onClick={(e) => { e.stopPropagation(); removeImage(idx); }}
                    >
                      x
                    </button>
                  </>
                ) : (
                  <div className="imc-slot-placeholder">
                    <div className="imc-slot-placeholder-icon">+</div>
                    <div>Arrastra o click</div>
                  </div>
                )}
                <input
                  ref={fileRefs[idx]}
                  type="file"
                  accept="image/*"
                  style={{ display: "none" }}
                  onChange={(e) => { handleFile(e.target.files[0], idx); e.target.value = ""; }}
                />
              </div>
            ))}
          </div>
        </div>

        <div className="imc-section">
          <div className="imc-section-title">Prompt</div>
          <textarea
            className="imc-textarea"
            value={prompt}
            onChange={(e) => setPrompt(e.target.value)}
            rows={4}
            placeholder="Describe lo que quieres generar. Usa @image0, @image1, @image2, @image3 para referenciar las imagenes..."
          />
          <div className="imc-hint">
            Ej: "Pon a la persona de @image0 con la ropa de @image1 en el escenario de @image2"
          </div>
        </div>

        {error && <div className="imc-error">{error}</div>}

        <button
          className="imc-btn-primary"
          disabled={!hasAnyImage || !prompt.trim() || processing}
          onClick={handleGenerate}
        >
          {processing ? "Generando..." : "Generar Composicion"}
        </button>
      </div>

      {/* Panel derecho: resultado */}
      <div className="imc-panel--right">
        {processing ? (
          <div className="imc-preview">
            <div className="imc-spinner">
              <div className="imc-spinner-ring" />
              Generando composicion con Gemini...
            </div>
          </div>
        ) : resultImage ? (
          <div className="imc-preview">
            <span className="imc-preview-label">Resultado</span>
            <img src={`data:image/png;base64,${resultImage}`} alt="Resultado" />
            <div className="imc-result-actions">
              <button className="imc-btn-use-as-input" onClick={handleUseAsInput}>Usar como input</button>
              <button className="imc-btn-download" onClick={handleDownload}>Descargar</button>
              <button className="imc-btn-biblioteca" onClick={handleSave} disabled={saving || saved2}>
                {saved2 ? "Guardado" : saving ? "Guardando..." : "Guardar en Biblioteca"}
              </button>
            </div>
          </div>
        ) : (
          <div className="imc-preview">
            <div className="imc-preview-placeholder">
              {hasAnyImage ? "Escribe un prompt y genera" : "El resultado aparecera aqui"}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
